import { Injectable, NgZone } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';

declare var google: any;

interface SearchApiResponse {
  items: Array<SearchApiResponseItems>;
  nextPageToken: string;
}

export interface SearchApiResponseItems {
  routeId: string;
  displayName: string;
  displayNameLower: string;
  status: string;
  geometry: string;
  lastUpdateDateInDb: string;
  geometryConverted: GeometryConverted;
}

interface GeometryConverted {
  coordinates: Array<Coordinates>;
  type: string;
}

interface Coordinates {
  lat: number;
  lng: number;
}

interface HistoryObject {
  durationInSeconds: number;
  numSamples: number;
  recordHour: string;
  routeId: string;
  staticDurationInSeconds: number;
}

interface HistoryApiResponse extends Array<HistoryObject> {}

interface HistoryCompareApiResponse {
  startDate1: Array<HistoryObject>;
  startDate2: Array<HistoryObject>;
}

interface RealTimeTrafficObject {
  selectedRouteId: string,
  displayName: string,
  speed: string,
  durationInSeconds: number,
  staticDurationInSeconds: number,
  last_update: string;
}

interface RealTimeTrafficApiResponse extends Array<RealTimeTrafficObject> {}

interface StreetRealTimeTrafficObject {
  selectedRouteId: string,
  displayName: string,
  speed: string,
  durationInSeconds: number,
  staticDurationInSeconds: number,
  lastUpdate: string;
  geometryConverted: GeometryConverted;
  segments: Array<StreetRealTimeTrafficObject>;
}

interface StreetRealTimeTrafficApiResponse extends Array<StreetRealTimeTrafficObject> {}

interface StretchesObject {
  centroid: Coordinates;
  positions: Array<Coordinates>;
}

interface DisruptionObject {
  centroid: Coordinates;
  confirmVoteCount: number;
  denyVoteCount: number;
  displayName: string;
  disruptionId: string;
  disruptionType: string;
  provenances: Array<string>;
  retrievalTimestamp: string;
  roadSegmentIds: Array<string>;
  selectedRouteId: string;
  startTimestamp: string;
  stretches: Array<StretchesObject>;
}

interface DisruptionApiResponse extends Array<DisruptionObject> {}

@Injectable({
  providedIn: 'root'
})
export class StreetInfoService {
  private readonly baseUrl: string = environment.backend.baseUrl;
  private readonly mapSubject = new BehaviorSubject<google.maps.Map | null>(null);
  public map$: Observable<google.maps.Map | null> = this.mapSubject.asObservable();

  setMap(map: google.maps.Map) {
    this.mapSubject.next(map);
  }

  private readonly searchBaseUrl = this.baseUrl + "/brennero-rmi/api/v1/addresses/search?pageSize=1000&status=STATUS_RUNNING";
  private readonly historyBaseUrl = this.baseUrl + "/brennero-rmi/api/v1/addresses/history";
  private readonly historyCompareBaseUrl = this.baseUrl + "/brennero-rmi/api/v1/addresses/history/compare";
  private readonly realTimeTrafficBaseUrl = this.baseUrl + "/brennero-rmi/api/v1/addresses/realtime?status=TRAFFIC_JAM";
  private readonly streetRealTimeTrafficBaseUrl = this.baseUrl + "/brennero-rmi/api/v1/addresses/realtime";
  private readonly streetRealTimeTrafficWithGeometriesBaseUrl = this.baseUrl + "/brennero-rmi/api/v1/addresses/realtime/geometries?status=TRAFFIC_JAM&status=TRAFFIC_JAM_HEAVY";
  private readonly disruptionBaseUrl = this.baseUrl + "/brennero-rmi/api/v1/addresses/disruptions";

  constructor(private readonly ngZone: NgZone) {

  }

  async getData(): Promise<SearchApiResponse | undefined> {
    try {
      const response = await fetch(this.searchBaseUrl);

      if(!response.ok) {
        throw new Error(`Errore HTTP: ${response.status}`);
      }

      const data: SearchApiResponse = await response.json();

      return data;
    } catch(error) {
      console.error(error);

      return undefined;
    }
  }

  async getRealTimeTrafficData(): Promise<RealTimeTrafficApiResponse | undefined> {
    try {
      const response = await fetch(this.realTimeTrafficBaseUrl);

      if(!response.ok) {
        throw new Error(`Errore HTTP: ${response.status}`);
      }

      const data: RealTimeTrafficApiResponse = await response.json();

      return data;
    } catch(error) {
      console.error(error);

      return undefined;
    }
  }

  async getStreetHistoryData(requestParameters: any): Promise<HistoryApiResponse | undefined> {
    const queryString = new URLSearchParams(requestParameters).toString().replaceAll('+', '%20');
    const url = this.historyBaseUrl + "?" + queryString;

    try {
      const response = await fetch(url);

      if(!response.ok) {
        throw new Error(`Errore HTTP: ${response.status}`);
      }

      const data: HistoryApiResponse = await response.json();

      return data;
    } catch(error) {
      console.error(error);

      return undefined;
    }
  }

  async getStreetHistoryCompareData(requestParameters: any): Promise<HistoryCompareApiResponse | undefined> {
    const queryString = new URLSearchParams(requestParameters).toString().replaceAll('+', '%20');
    const url = this.historyCompareBaseUrl + "?" + queryString;

    try {
      const response = await fetch(url);

      if(!response.ok) {
        throw new Error(`Errore HTTP: ${response.status}`);
      }

      const data: HistoryCompareApiResponse = await response.json();

      return data;
    } catch(error) {
      console.error(error);

      return undefined;
    }
  }

  async getStreetRealTimeTraffic(routeId: string): Promise<StreetRealTimeTrafficApiResponse | undefined> {
    const url = this.streetRealTimeTrafficBaseUrl + "/" + routeId;

    try {
      const response = await fetch(url);

      if(!response.ok) {
        throw new Error(`Errore HTTP: ${response.status}`);
      }

      const data: StreetRealTimeTrafficApiResponse = await response.json();

      return data;
    } catch(error) {
      console.error(error);

      return undefined;
    }
  }

  async getStreetRealTimeTrafficWithGeometries(): Promise<StreetRealTimeTrafficApiResponse | undefined> {
    const url = this.streetRealTimeTrafficWithGeometriesBaseUrl;

    try {
      const response = await fetch(url);

      if(!response.ok) {
        throw new Error(`Errore HTTP: ${response.status}`);
      }

      const data: StreetRealTimeTrafficApiResponse = await response.json();

      return data;
    } catch(error) {
      console.error(error);

      return undefined;
    }
  }

  async getDisruptions(): Promise<DisruptionApiResponse | undefined> {
    const url = this.disruptionBaseUrl;

    try {
      const response = await fetch(url);

      if(!response.ok) {
        throw new Error(`Errore HTTP: ${response.status}`);
      }

      const data: DisruptionApiResponse = await response.json();

      return data;
    } catch(error) {
      console.error(error);

      return undefined;
    }
  }
}
